import { FC, ReactNode } from 'react';
import { TFunction } from 'i18next';
import { ConditionalStyles, TableColumn } from 'react-data-table-component';
import { ErisSummaryResultsModel } from 'src/api/eris/store/ErisInformationStore';

type TableCellContentProps = {
  children: ReactNode;
  justify?: 'start' | 'end';
  className?: string;
};

const TableCellContent: FC<TableCellContentProps> = ({
  children,
  justify = 'end',
  className = '',
}) => {
  const justification = justify === 'start' ? 'justify-start' : 'justify-end';
  return (
    <div
      className={`flex w-full items-center whitespace-nowrap px-2 ${justification} ${className}`}
    >
      {children}
    </div>
  );
};

export const resolveRowStyle = (isSumm: boolean) => {
  return isSumm
    ? {
        backgroundColor: '#2e2db9',
        color: '#ffffff',
        position: 'sticky' as const,
        zIndex: 1,
      }
    : { backgroundColor: 'transparent' };
};

export const conditionalRowStyles: ConditionalStyles<ErisSummaryResultsModel>[] = [
  {
    when: () => true,
    style: (row: ErisSummaryResultsModel) => resolveRowStyle(row.groupName === 'TOTAL'),
  },
];

export const columns = (
  t: TFunction<'translation', undefined>
): TableColumn<ErisSummaryResultsModel>[] => {
  return [
    {
      name: <div>{t('eris.supportSummaryList.name')}</div>,
      cell: (row: ErisSummaryResultsModel) =>
        row.groupName === 'TOTAL' ? (
          <TableCellContent justify="end">{t('eris.supportSummaryList.total')}</TableCellContent>
        ) : (
          <TableCellContent justify="start">{row.groupName}</TableCellContent>
        ),
      width: '25rem',
      sortable: false,
    },
    {
      name: t('eris.supportSummaryList.units'),
      cell: (row: ErisSummaryResultsModel) => (
        <TableCellContent>{row.units ? row.units : '0'}</TableCellContent>
      ),
      sortable: false,
    },
    {
      name: <div>{t('eris.supportSummaryList.suspendedUnits')}</div>,
      cell: (row: ErisSummaryResultsModel) => (
        <TableCellContent className="border-l-slate-400">
          {`${row.suspendedUnits ? row.suspendedUnits : '0'} (${
            row.repairRequiredUnits ? row.repairRequiredUnits : '0'
          })`}
        </TableCellContent>
      ),
      sortable: false,
    },
    {
      name: <div>{t('eris.supportSummaryList.heliosConfirmedAndTelephoneContactUnits')}</div>,
      cell: (row: ErisSummaryResultsModel) => (
        <TableCellContent>
          {row.heliosConfirmedUnits ? row.heliosConfirmedUnits : '0'}
        </TableCellContent>
      ),
      sortable: false,
    },
    {
      name: t('eris.supportSummaryList.recoveredUnits'),
      cell: (row: ErisSummaryResultsModel) => (
        <TableCellContent>{row.recoveredUnits ? row.recoveredUnits : '0'}</TableCellContent>
      ),
      sortable: false,
    },
    {
      name: t('eris.supportSummaryList.recoveryRate'),
      cell: (row: ErisSummaryResultsModel) => {
        if (row.recoveredUnits === 0 && row.suspendedUnits === 0) {
          return <TableCellContent>–</TableCellContent>;
        }
        return (
          <TableCellContent>
            {row.recoveryRate ? row.recoveryRate.toFixed(1) : '0.0'} {t('eris.common.percent')}
          </TableCellContent>
        );
      },
      sortable: false,
    },
    {
      name: <div>{t('eris.supportSummaryList.confinmentAndRescue')}</div>,
      cell: (row: ErisSummaryResultsModel) => (
        <TableCellContent>
          {`${row.confinedUnits ? row.confinedUnits : '0'} (${row.rescuedUnits ? row.rescuedUnits : '0'})`}
        </TableCellContent>
      ),
      sortable: false,
    },
  ];
};
